import { useState } from 'react';
import { SERVICE_TIERS } from '../../utils/pipelineCalculations';
import { formatCurrency } from '../../utils/formatting';
import { deriveClientRevenueFields, CATEGORY_OPTIONS, defaultCategoryForTier } from '../../revenue/closedWonToClient.mjs';

const inputClass = 'w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue';
const labelClass = 'block text-[11px] font-bold uppercase tracking-[0.08em] text-navy mb-1';

/**
 * Modal that appears when a prospect is advanced to Closed Won.
 * Collects client details and household assignment before creating the client.
 */
export default function ClosedWonModal({ prospect, existingHouseholds, onConfirm, onCancel }) {
  const tier = SERVICE_TIERS[prospect.serviceTier] || {};
  const division = prospect.division || tier.division || 'bwm';
  const ev = prospect.estimatedValue || {};

  const [form, setForm] = useState({
    name: prospect.name || '',
    company: prospect.company || '',
    division,
    serviceTier: prospect.serviceTier || 'guided-wealth',
    monthlyRevenue: ev.monthlyRecurring || 0,
    annualRevenue: (ev.oneTime || 0) + ((ev.monthlyRecurring || 0) * 12) + ((ev.aum || 0) * (ev.aumFeeRate || 0)),
    aum: ev.aum || 0,
    householdId: '',
    householdName: '',
    // Default the service start date to TODAY. Firm convention (Jun 2026):
    // we mark close-won when the money lands, so revenue should bucket into
    // the month we're sitting in unless the user explicitly edits it.
    //
    // Previously this read prospect.serviceStartDate || prospect.expectedCloseDate,
    // which caused SF-synced opportunities (whose CloseDate often sits a few
    // months back as a forecast placeholder) to bucket their revenue into
    // the past — e.g., Turasky's $20K Tax Plan landed in March because his
    // SF CloseDate was 2026-03-31. The user still has a date picker if they
    // need to override (e.g., signed-this-month-for-next-year tax-prep
    // deals).
    startDate: new Date().toISOString().split('T')[0],
    revenueCategory: defaultCategoryForTier(division, prospect.serviceTier),
    notes: prospect.notes || '',
    alreadyClient: false, // when true, just close the prospect; don't add to clients or P&L
  });

  function update(field, value) {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'monthlyRevenue') {
        const monthly = parseFloat(value) || 0;
        if (monthly > 0) {
          next.annualRevenue = Math.round(monthly * 12 * 100) / 100;
        }
      }
      return next;
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    const monthly = parseFloat(form.monthlyRevenue) || 0;
    const annual = parseFloat(form.annualRevenue) || 0;
    const aum = parseFloat(form.aum) || 0;
    // Derive the engine-keyed revenue fields (clientType / serviceTier / fees)
    // from the chosen income category, so the saved client reads back through
    // mapClientToEngagements as exactly this income line. This is what makes a
    // won deal land in the right place (e.g. Fractional CAO vs Bookkeeping, or
    // an AUM fee computed from assets). See src/revenue/closedWonToClient.mjs.
    const derived = deriveClientRevenueFields({
      category: form.revenueCategory, division, monthly, annual, aum, serviceTier: form.serviceTier,
    });
    onConfirm({
      ...form,
      ...derived, // clientType, serviceTier, monthlyRevenue, annualRevenue, investmentFeeAnnual, taxPlanFee, aum
      householdId: form.householdId || `hh-manual-${Date.now()}`,
      householdName: form.householdName || form.name,
      status: 'Active',
      divisions: [division],
      revenueCategory: form.revenueCategory,
      // If true: just close the prospect; skip client creation.
      alreadyClient: form.alreadyClient,
    });
  }

  const categoryOptions = CATEGORY_OPTIONS[division] || CATEGORY_OPTIONS.bwm;

  // Derived engine fields for the current form state — drives the preview
  // below so what you see is what gets saved (submit recomputes identically).
  const derived = deriveClientRevenueFields({
    category: form.revenueCategory, division,
    monthly: parseFloat(form.monthlyRevenue) || 0,
    annual: parseFloat(form.annualRevenue) || 0,
    aum: parseFloat(form.aum) || 0,
    serviceTier: form.serviceTier,
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-[4px] shadow-[0_8px_30px_rgba(0,0,0,0.15)] w-[560px] max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-deep-teal px-6 py-4 rounded-t-[4px]">
          <h2 className="text-white text-[16px] font-bold">New Client: {prospect.name}</h2>
          <p className="text-white/70 text-[12px] mt-1">
            This prospect has been closed as Won. Confirm the details below to create a client and add their revenue to the P&L.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Already-a-client toggle */}
          <label className={`flex items-start gap-3 p-3 rounded-[4px] cursor-pointer border ${
            form.alreadyClient ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
          }`}>
            <input
              type="checkbox"
              checked={form.alreadyClient}
              onChange={e => update('alreadyClient', e.target.checked)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="text-[12px] font-bold text-navy">Already an existing client</div>
              <div className="text-[11px] text-[#666] mt-0.5">
                Mark this prospect as Closed Won for pipeline metrics, but do NOT create a duplicate client or add their revenue (it's already in the system).
              </div>
            </div>
          </label>

          {/* Name + Company */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Client Name</label>
              <input type="text" value={form.name} onChange={e => update('name', e.target.value)}
                className={inputClass} required />
            </div>
            <div>
              <label className={labelClass}>Company (optional)</label>
              <input type="text" value={form.company} onChange={e => update('company', e.target.value)}
                className={inputClass} />
            </div>
          </div>

          {/* Division + Tier + Household */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Division</label>
              <input type="text" value={division.toUpperCase()} className={inputClass} disabled />
            </div>
            <div>
              <label className={labelClass}>Service Tier</label>
              <input type="text" value={tier.label || prospect.serviceTier} className={inputClass} disabled />
            </div>
            <div>
              <label className={labelClass}>Household</label>
              <select value={form.householdId} onChange={e => {
                const selected = existingHouseholds?.find(h => h.id === e.target.value);
                if (selected) {
                  setForm(prev => ({ ...prev, householdId: selected.id, householdName: selected.name }));
                } else {
                  setForm(prev => ({ ...prev, householdId: '', householdName: '' }));
                }
              }} className={inputClass}>
                <option value="">New household</option>
                {(existingHouseholds || []).map(h => (
                  <option key={h.id} value={h.id}>{h.name} ({h.entries.length})</option>
                ))}
              </select>
            </div>
          </div>

          {/* Revenue */}
          <div className="border-t border-gray-200 pt-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-deep-teal mb-3">Revenue Details</p>
            <div className="mb-3">
              <label className={labelClass}>P&L Income Category</label>
              <select value={form.revenueCategory} onChange={e => update('revenueCategory', e.target.value)} className={inputClass}>
                {categoryOptions.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Monthly Revenue ($)</label>
                <input type="number" value={form.monthlyRevenue} onChange={e => update('monthlyRevenue', e.target.value)}
                  className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Annual Revenue ($)</label>
                <input type="number" value={form.annualRevenue} onChange={e => update('annualRevenue', e.target.value)}
                  className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>AUM ($)</label>
                <input type="number" value={form.aum} onChange={e => update('aum', e.target.value)}
                  className={inputClass} />
              </div>
            </div>
          </div>

          {/* Start Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Revenue Start Date</label>
              <input type="date" value={form.startDate} onChange={e => update('startDate', e.target.value)}
                className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Notes</label>
              <input type="text" value={form.notes} onChange={e => update('notes', e.target.value)}
                placeholder="Optional notes" className={inputClass} />
            </div>
          </div>

          {/* Revenue impact preview */}
          <div className={`rounded-[4px] p-3 text-[12px] ${form.alreadyClient ? 'bg-amber-50' : 'bg-bone'}`}>
            <p className="font-bold text-navy mb-1">
              {form.alreadyClient ? 'No P&L Impact (Already a Client)' : 'P&L Impact Preview'}
            </p>
            <p className="text-[#666]">
              {(() => {
                if (form.alreadyClient) {
                  return <>Prospect will be marked Closed Won for pipeline conversion metrics. No client record created, no revenue added (already in system).</>;
                }
                const cat = form.revenueCategory;
                const startShort = new Date(form.startDate).toLocaleDateString('en-US', { month: 'short' });
                const tail = ' This will be replaced by QuickBooks data as months close.';

                if (cat === 'commissions') {
                  return <>Commissions are recorded from QuickBooks actuals as they come in. Nothing is projected here; the client record is created for tracking.</>;
                }

                const parts = [];
                if (derived.monthlyRevenue > 0) {
                  const label = cat === 'cao' ? 'Fractional CAO' : cat === 'bookkeeping' ? 'Bookkeeping' : 'Subscription Fees';
                  parts.push(`${formatCurrency(derived.monthlyRevenue)}/mo to ${label} from ${startShort} through Dec.`);
                } else if (cat === 'subscription_fees' && derived.annualRevenue > 0) {
                  parts.push(`${formatCurrency(derived.annualRevenue)}/yr to Subscription Fees, spread evenly from ${startShort}.`);
                } else if (cat === 'tax_planning' && derived.annualRevenue > 0) {
                  parts.push(`${formatCurrency(derived.annualRevenue)} Tax Plan, spread across the open months from ${startShort}.`);
                } else if (cat === 'tax_preparation' && derived.annualRevenue > 0) {
                  parts.push(`${formatCurrency(derived.annualRevenue)} Tax Preparation, spread across the open months from ${startShort}.`);
                }
                if (derived.investmentFeeAnnual > 0) {
                  parts.push(`${formatCurrency(derived.investmentFeeAnnual)}/yr Investment Management (assets x 0.5%), billed quarterly (Jan, Apr, Jul, Oct) from the first open quarter after signing.`);
                }

                if (parts.length === 0) {
                  return <>Enter the revenue for this deal to see its P&L impact.</>;
                }
                return <>{parts.join(' ')}{tail}</>;
              })()}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onCancel}
              className="px-4 py-2 text-[12px] font-bold text-[#888] bg-gray-100 rounded-[4px] hover:bg-gray-200">
              Cancel
            </button>
            <button type="submit"
              className={`px-5 py-2 text-[12px] font-bold text-white rounded-[4px] ${
                form.alreadyClient ? 'bg-amber-600 hover:bg-amber-700' : 'bg-deep-teal hover:bg-deep-teal/90'
              }`}>
              {form.alreadyClient ? 'Close Pipeline Entry Only' : 'Create Client & Add Revenue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
